import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Pagination from '@mui/material/Pagination';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type { TPaymentPlan } from '@onesource/schemas';
import { getSession, useGetMemberPaymentPlansQuery } from '@onesource/services';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { HiArrowRight, HiCreditCard } from 'react-icons/hi2';
import { BillingSubscriptionsPreloader } from './subscriptions-preloader';
import { centsToUSD, statusColor } from '../../utils';

function PaymentPlanRow({ plan }: { plan: TPaymentPlan }) {
  return (
    <TableRow hover sx={{ '&:last-child td': { border: 0 } }}>
      <TableCell>
        <Link
          to="/payment-plans/$paymentPlanId"
          params={{ paymentPlanId: String(plan.paymentPlanId) }}
          style={{ textDecoration: 'none' }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: 'error.main', '&:hover': { textDecoration: 'underline' } }}
          >
            #{plan.paymentPlanId}
          </Typography>
        </Link>
      </TableCell>

      <TableCell>
        <Chip
          label={plan.status.charAt(0).toUpperCase() + plan.status.slice(1)}
          size="small"
          variant="outlined"
          color={statusColor(plan.status)}
          sx={{ fontWeight: 500, fontSize: 12 }}
        />
      </TableCell>

      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {plan.products[0]?.productName ?? '—'}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {plan.nextPaymentOn}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography variant="body2" fontWeight={500}>
          {centsToUSD(plan.total)}
        </Typography>
      </TableCell>

      <TableCell align="right">
        <Link
          to="/payment-plans/$paymentPlanId"
          params={{ paymentPlanId: String(plan.paymentPlanId) }}
          style={{ textDecoration: 'none' }}
        >
          <Button
            variant="outlined"
            size="small"
            color="error"
            endIcon={<HiArrowRight size={16} />}
            sx={{
              minWidth: 88,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              '& .MuiButton-endIcon': { marginRight: 0 },
            }}
          >
            View
          </Button>
        </Link>
      </TableCell>
    </TableRow>
  );
}

export function PaymentPlansTab() {
  const token = getSession();
  const [page, setPage] = useState(1);
  const perPage = 10;

  const { data, isLoading } = useGetMemberPaymentPlansQuery({ page, perPage, enabled: !!token });

  if (isLoading) {
    return <BillingSubscriptionsPreloader rows={perPage} />;
  }

  if (!data?.items?.length) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 2 }}>
        <HiCreditCard size={56} style={{ opacity: 0.3 }} />
        <Typography variant="h6" color="text.secondary">
          No payment plans
        </Typography>
        <Typography variant="body2" color="text.disabled" textAlign="center" maxWidth={380}>
          Your active payment plans will appear here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      <Box sx={{ borderRadius: 2, border: theme => `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.200' }}>Plan</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.200' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.200' }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.200' }}>Next Payment</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.200' }}>Total</TableCell>
                <TableCell sx={{ bgcolor: 'grey.200' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map(plan => (
                <PaymentPlanRow key={plan.paymentPlanId} plan={plan} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {(data.totalPages ?? 1) > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              px: 2,
              py: 1.5,
              borderTop: theme => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Pagination
              count={data.totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              shape="rounded"
              size="large"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
